import {Injectable} from '@angular/core';
import {MessageEntry} from '../message.entry';
import {Observable, Subject} from 'rxjs';
import {NewMessageForm} from './new-message.form';
import {ChatUpdateEntry} from './chat-update.entry';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {delay, map, retryWhen, take} from 'rxjs/operators';
import {MessagesFilter} from './messages.filter';
import {ChatMarkReadEntry} from './chat-mark-read.entry';
import {InitMessagesResponse} from './init-messages-response';
import {SentMessageResponse} from '../model/SentMessageResponse';
import {SendMessageEntry} from "../../ai-events/model/send-message.entry";

@Injectable()
export class ChatMessagesService {

  private sendMessageSource = new Subject<SendMessageEntry>();
  public sendMessage$ = this.sendMessageSource.asObservable();

  constructor(private http: HttpClient) {}

  public initSendMessage(message: SendMessageEntry): void {
    this.sendMessageSource.next(message);
  }

  public saveMessage(vehicleId: number, messageText: string, shouldTranslate: boolean): Observable<SentMessageResponse> {
    let form = new NewMessageForm(vehicleId, messageText);
    return this.http.post(`/api/messages?shouldTranslate=${shouldTranslate}`, form)
      .pipe(map(res => <SentMessageResponse>res));
  }

  public fetchInitMessages(vehicleId: number, language: string): Observable<InitMessagesResponse> {
    return this.http.get<InitMessagesResponse>(`/api/messages/init?vehicleId=${vehicleId}&language=${language}`)
      .pipe(retryWhen(errors => errors.pipe(delay(1000), take(3))));
  }

  public fetchMessages(vehicleId: number, lastMessage: number, language: string): Observable<MessageEntry[]> {
    return this.http.get(`/api/messages` +
      `?vehicleId=${vehicleId}` +
      `&lastMessage=${lastMessage}` +
      `&language=${language}`)
      .pipe(map(res => <MessageEntry[]>res));
  }

  public addMessages(vehicleId: number, lastMessageId: number, language: string): Observable<MessageEntry[]> {
    return this.http.get(`/api/messages/add` +
      `?vehicleId=${vehicleId}` +
      `&lastMessage=${lastMessageId}` +
      `&language=${language}`)
      .pipe(map(res => <MessageEntry[]>res));
  }

  public updateChat(vehicleId: number, lastShown: number, language: string): Observable<ChatUpdateEntry> {
    return this.http.get(`/api/messages/update-chat?vehicleId=${vehicleId}&lastShown=${lastShown}&language=${language}`)
      .pipe(map(res => <ChatUpdateEntry>res));
  }

  public markAsUnread(messages: ChatMarkReadEntry[]): Observable<any> {
    return this.http.post('/api/messages/mark-as-unread', messages);
  }

  public saveMultipartMessage(vehicleId: number, message: string, file: any): Observable<MessageEntry> {
    let formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`/api/chat-files/multipart?vehicleId=${vehicleId}&message=${encodeURIComponent(message)}`, formData, {
      headers: new HttpHeaders().append('Accept', 'application/json')
    })
      .pipe(map(res => <MessageEntry>res));
  }


  public filterMessages(vehicleId: number, filter: MessagesFilter, language: string): Observable<MessageEntry[]> {
    return this.http.post(`/api/messages/filter?vehicleId=${vehicleId}&language=${language}`, filter)
      .pipe(map(res => <MessageEntry[]>res));
  }

  public loadFiltered(vehicleId: number, date: string, language: string): Observable<MessageEntry[]> {
    return this.http.get(`/api/messages/filter/load?vehicleId=${vehicleId}&date=${date}&language=${language}`)
      .pipe(map(res => <MessageEntry[]>res));
  }

  public getLastSeenMessage(vehicleId: number): Observable<number> {
    return this.http.get(`/api/messages/last-seen?vehicleId=${vehicleId}`)
      .pipe(map(res => <number>res));
  }

  public markMessagesAllAsRead(): Observable<void> {
    return this.http.get<void>(`/api/messages/mark-all-read`)
  }

  public translateMessage(messageId: number, language: string, message: string): Observable<string> {
    return this.http.post(`/api/messages/translate?messageId=${messageId}&language=${language}`, message, {responseType: 'text'})
  }
}
